/**
 * Auto-Q&A-Bedingungen — typisierte, begrenzte Ausdruckslogik (Paket 24).
 *
 * Browser, tRPC und Motor teilen exakt diese Feld-, Operator- und
 * Auswertungsregeln. Kein freier JSON-Pfad, kein `eval`, keine aus einer
 * Benutzereingabe gebaute RegExp.
 */
import { z } from "zod";
import { QA_ANLASS_QUELLEN, QA_ZIELART_LABEL, QA_ZIELARTEN, } from "./auto-qa-anlaesse.js";
export const QA_BEDINGUNGS_VERKNUEPFUNGEN = [
    "alle",
    "mindestens_eine",
    "keine",
];
export const QA_BEDINGUNGS_OPERATOREN = [
    "ist",
    "ist_nicht",
    "enthaelt",
    "enthaelt_nicht",
    "beginnt_mit",
    "endet_mit",
    "ist_groesser_als",
    "ist_kleiner_als",
    "mindestens",
    "hoechstens",
    "ist_wahr",
    "ist_falsch",
    "ist_leer",
    "ist_nicht_leer",
];
export const QA_BEDINGUNGS_ZIELARTEN = QA_ZIELARTEN;
export const QA_BEDINGUNGS_DATENTYPEN = [
    "text",
    "kennung",
    "zahl",
    "wahrheitswert",
    "auswahl",
    "textliste",
];
const TEXT_OPERATOREN = [
    "ist",
    "ist_nicht",
    "enthaelt",
    "enthaelt_nicht",
    "beginnt_mit",
    "endet_mit",
    "ist_leer",
    "ist_nicht_leer",
];
const KENNUNG_OPERATOREN = [
    "ist",
    "ist_nicht",
    "ist_leer",
    "ist_nicht_leer",
];
const ZAHL_OPERATOREN = [
    "ist",
    "ist_nicht",
    "ist_groesser_als",
    "ist_kleiner_als",
    "mindestens",
    "hoechstens",
    "ist_leer",
    "ist_nicht_leer",
];
const WAHRHEITSWERT_OPERATOREN = ["ist_wahr", "ist_falsch"];
const TEXTLISTEN_OPERATOREN = [
    "enthaelt",
    "enthaelt_nicht",
    "ist_leer",
    "ist_nicht_leer",
];
const ALLE_ZIELARTEN = QA_BEDINGUNGS_ZIELARTEN;
export const QA_BEDINGUNGS_FELDER = {
    "ziel.art": {
        label: "Zielart",
        gruppe: "Allgemein",
        beschreibung: "Art des Objekts, an dem der Anlass eingetreten ist.",
        datentyp: "auswahl",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
        auswahl: QA_ZIELARTEN.map((wert) => ({
            wert,
            label: QA_ZIELART_LABEL[wert],
        })),
        freieAuswahl: false,
    },
    "ausloeser.art": {
        label: "Auslöserart",
        gruppe: "Allgemein",
        beschreibung: "Wer oder was den normalisierten Anlass ausgelöst hat.",
        datentyp: "auswahl",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
        auswahl: [
            { wert: "person", label: "Person" },
            { wert: "metarr", label: "Metarr" },
            { wert: "mcp", label: "MCP" },
            { wert: "system", label: "System / GitLab" },
        ],
        freieAuswahl: false,
    },
    "ausloeser.person_id": {
        label: "Auslösende Person",
        gruppe: "Allgemein",
        beschreibung: "Interne Kennung der auslösenden Person, sofern vorhanden.",
        datentyp: "kennung",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
    },
    "projekt.id": {
        label: "Projektkennung",
        gruppe: "Allgemein",
        beschreibung: "Interne Kennung des Projekts.",
        datentyp: "kennung",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
    },
    "ziel.titel": {
        label: "Titel des Ziels",
        gruppe: "Ziel",
        beschreibung: "Normalisierter Titel des betroffenen Objekts.",
        datentyp: "text",
        zielArten: ALLE_ZIELARTEN,
        operatoren: TEXT_OPERATOREN,
    },
    "ziel.kennung": {
        label: "Sprechende Kennung",
        gruppe: "Ziel",
        beschreibung: "Etwa CUST-42, CUST-AP3, !17 oder eine Commit-SHA.",
        datentyp: "kennung",
        zielArten: ALLE_ZIELARTEN,
        operatoren: TEXT_OPERATOREN,
    },
    "ziel.zustand": {
        label: "Zustand des Ziels",
        gruppe: "Ziel",
        beschreibung: "Normalisierter oder fachlicher Zustand des Zielobjekts.",
        datentyp: "auswahl",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
        auswahl: [],
        freieAuswahl: true,
    },
    "ziel.revision": {
        label: "Zielrevision",
        gruppe: "Ziel",
        beschreibung: "Eingefrorene Version, SHA oder Inhaltsfingerabdruck.",
        datentyp: "kennung",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
    },
    "ziel.inhalt": {
        label: "Inhalt des Ziels",
        gruppe: "Ziel",
        beschreibung: "Beschreibung, Markdown, Frage oder sonstiger Prüfinhalt.",
        datentyp: "text",
        zielArten: ALLE_ZIELARTEN,
        operatoren: TEXT_OPERATOREN,
    },
    "ziel.frist": {
        label: "Frist des Ziels",
        gruppe: "Ziel",
        beschreibung: "ISO-Zeitpunkt der Frist; leer, wenn keine Frist besteht.",
        datentyp: "kennung",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
    },
    "ziel.prioritaet": {
        label: "Priorität des Ziels",
        gruppe: "Ziel",
        beschreibung: "Numerische Priorität, sofern das Ziel eine besitzt.",
        datentyp: "zahl",
        zielArten: ALLE_ZIELARTEN,
        operatoren: ZAHL_OPERATOREN,
    },
    "ziel.labels": {
        label: "Labels des Ziels",
        gruppe: "Ziel",
        beschreibung: "Namen aller am Ziel gesetzten Labels.",
        datentyp: "textliste",
        zielArten: ALLE_ZIELARTEN,
        operatoren: TEXTLISTEN_OPERATOREN,
    },
    "ereignis.quelle": {
        label: "Anlassquelle",
        gruppe: "Ereignis",
        beschreibung: "Technische oder fachliche Quelle der normalisierten Auslösung.",
        datentyp: "auswahl",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
        auswahl: QA_ANLASS_QUELLEN.map((wert) => ({ wert, label: wert })),
        freieAuswahl: false,
    },
    "ereignis.geaenderte_felder": {
        label: "Geänderte Felder",
        gruppe: "Ereignis",
        beschreibung: "Feldnamen aus einem normalisierten Cubic-Ereignis.",
        datentyp: "textliste",
        zielArten: ALLE_ZIELARTEN,
        operatoren: TEXTLISTEN_OPERATOREN,
    },
    "ereignis.hinweis": {
        label: "Ereignishinweis",
        gruppe: "Ereignis",
        beschreibung: "Der normalisierte, optionale Hinweis des Cubic-Ereignisses.",
        datentyp: "text",
        zielArten: ALLE_ZIELARTEN,
        operatoren: TEXT_OPERATOREN,
    },
    "ereignis.beziehungsart": {
        label: "Beziehungsart",
        gruppe: "Ereignis",
        beschreibung: "Art einer gesetzten oder entfernten Beziehung.",
        datentyp: "kennung",
        zielArten: ALLE_ZIELARTEN,
        operatoren: TEXT_OPERATOREN,
    },
    "ereignis.gegenueber_art": {
        label: "Art des Gegenübers",
        gruppe: "Ereignis",
        beschreibung: "Objektart am anderen Ende einer Beziehung.",
        datentyp: "kennung",
        zielArten: ALLE_ZIELARTEN,
        operatoren: TEXT_OPERATOREN,
    },
    "ereignis.gegenueber_id": {
        label: "Kennung des Gegenübers",
        gruppe: "Ereignis",
        beschreibung: "Interne Kennung am anderen Ende einer Beziehung.",
        datentyp: "kennung",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
    },
    "ereignis.status_vorher": {
        label: "Vorheriger Status",
        gruppe: "Ereignis",
        beschreibung: "Zustand oder Status unmittelbar vor dem Anlass.",
        datentyp: "auswahl",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
        auswahl: [],
        freieAuswahl: true,
    },
    "ereignis.status_nachher": {
        label: "Neuer Status",
        gruppe: "Ereignis",
        beschreibung: "Zustand oder Status unmittelbar nach dem Anlass.",
        datentyp: "auswahl",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
        auswahl: [],
        freieAuswahl: true,
    },
    "ereignis.schwere": {
        label: "Schweregrad",
        gruppe: "Ereignis",
        beschreibung: "Schweregrad eines Befunds oder einer Qualitätslage.",
        datentyp: "auswahl",
        zielArten: ALLE_ZIELARTEN,
        operatoren: KENNUNG_OPERATOREN,
        auswahl: [
            { wert: "hinweis", label: "Hinweis" },
            { wert: "gering", label: "Gering" },
            { wert: "mittel", label: "Mittel" },
            { wert: "schwer", label: "Schwer" },
            { wert: "kritisch", label: "Kritisch" },
        ],
        freieAuswahl: true,
    },
    "ereignis.ref": {
        label: "Git-Ref oder Umgebung",
        gruppe: "Ereignis",
        beschreibung: "Branch, Tag, Ref oder Deployment-Umgebung des Anlasses.",
        datentyp: "kennung",
        zielArten: ALLE_ZIELARTEN,
        operatoren: TEXT_OPERATOREN,
    },
    "ereignis.anzahl": {
        label: "Gezählter Wert",
        gruppe: "Ereignis",
        beschreibung: "Anzahl, Versuchszähler oder berechneter Schwellenwert.",
        datentyp: "zahl",
        zielArten: ALLE_ZIELARTEN,
        operatoren: ZAHL_OPERATOREN,
    },
    "mr.gitlab_iid": {
        label: "MR-Nummer",
        gruppe: "Merge Request",
        beschreibung: "Projektinterne Nummer des Merge Requests in GitLab.",
        datentyp: "zahl",
        zielArten: ["merge_request"],
        operatoren: ZAHL_OPERATOREN,
    },
    "mr.titel": {
        label: "MR-Titel",
        gruppe: "Merge Request",
        beschreibung: "Titel des Merge Requests.",
        datentyp: "text",
        zielArten: ["merge_request"],
        operatoren: TEXT_OPERATOREN,
    },
    "mr.beschreibung": {
        label: "MR-Beschreibung",
        gruppe: "Merge Request",
        beschreibung: "Beschreibung des Merge Requests; kann fehlen.",
        datentyp: "text",
        zielArten: ["merge_request"],
        operatoren: TEXT_OPERATOREN,
    },
    "mr.quellzweig": {
        label: "Quellzweig",
        gruppe: "Merge Request",
        beschreibung: "Branch, aus dem der Merge Request kommt.",
        datentyp: "kennung",
        zielArten: ["merge_request"],
        operatoren: KENNUNG_OPERATOREN,
    },
    "mr.zielzweig": {
        label: "Zielzweig",
        gruppe: "Merge Request",
        beschreibung: "Branch, in den der Merge Request gemergt werden soll.",
        datentyp: "kennung",
        zielArten: ["merge_request"],
        operatoren: KENNUNG_OPERATOREN,
    },
    "mr.zustand": {
        label: "MR-Zustand",
        gruppe: "Merge Request",
        beschreibung: "Von GitLab gemeldeter Zustand des Merge Requests.",
        datentyp: "auswahl",
        zielArten: ["merge_request"],
        operatoren: KENNUNG_OPERATOREN,
        auswahl: [
            { wert: "opened", label: "Offen" },
            { wert: "merged", label: "Gemergt" },
            { wert: "closed", label: "Geschlossen" },
            { wert: "locked", label: "Gesperrt" },
        ],
        freieAuswahl: true,
    },
    "mr.entwurf": {
        label: "MR ist Entwurf",
        gruppe: "Merge Request",
        beschreibung: "Ob GitLab den Merge Request als Draft/WIP meldet.",
        datentyp: "wahrheitswert",
        zielArten: ["merge_request"],
        operatoren: WAHRHEITSWERT_OPERATOREN,
    },
    "mr.basis_sha": {
        label: "Basis-SHA",
        gruppe: "Merge Request",
        beschreibung: "Eingefrorene Basisrevision des Merge Requests.",
        datentyp: "kennung",
        zielArten: ["merge_request"],
        operatoren: KENNUNG_OPERATOREN,
    },
    "mr.kopf_sha": {
        label: "Kopf-SHA",
        gruppe: "Merge Request",
        beschreibung: "Aktuelle Kopfrevision des Merge Requests.",
        datentyp: "kennung",
        zielArten: ["merge_request"],
        operatoren: KENNUNG_OPERATOREN,
    },
    "pipeline.gitlab_id": {
        label: "Pipeline-Nummer",
        gruppe: "Pipeline",
        beschreibung: "Projektinterne Pipeline-Nummer in GitLab.",
        datentyp: "zahl",
        zielArten: ["pipeline"],
        operatoren: ZAHL_OPERATOREN,
    },
    "pipeline.zustand": {
        label: "Pipeline-Zustand",
        gruppe: "Pipeline",
        beschreibung: "Von GitLab gemeldeter Zustand der Pipeline.",
        datentyp: "auswahl",
        zielArten: ["pipeline"],
        operatoren: KENNUNG_OPERATOREN,
        auswahl: [
            { wert: "success", label: "Erfolgreich" },
            { wert: "failed", label: "Gescheitert" },
            { wert: "running", label: "Läuft" },
            { wert: "pending", label: "Wartet" },
            { wert: "canceled", label: "Abgebrochen" },
            { wert: "skipped", label: "Übersprungen" },
            { wert: "manual", label: "Manuell" },
        ],
        freieAuswahl: true,
    },
    "pipeline.ref": {
        label: "Pipeline-Ref",
        gruppe: "Pipeline",
        beschreibung: "Branch oder Tag, auf dem die Pipeline läuft.",
        datentyp: "kennung",
        zielArten: ["pipeline"],
        operatoren: KENNUNG_OPERATOREN,
    },
    "pipeline.commit_sha": {
        label: "Pipeline-Commit",
        gruppe: "Pipeline",
        beschreibung: "Commit-SHA der Pipeline.",
        datentyp: "kennung",
        zielArten: ["pipeline"],
        operatoren: KENNUNG_OPERATOREN,
    },
    "pipeline.ist_tag": {
        label: "Tag-Pipeline",
        gruppe: "Pipeline",
        beschreibung: "Ob die Pipeline auf einem Git-Tag läuft.",
        datentyp: "wahrheitswert",
        zielArten: ["pipeline"],
        operatoren: WAHRHEITSWERT_OPERATOREN,
    },
    "pipeline.quelle": {
        label: "Pipeline-Quelle",
        gruppe: "Pipeline",
        beschreibung: "GitLab-Quelle, etwa Push, Web oder Merge Request.",
        datentyp: "auswahl",
        zielArten: ["pipeline"],
        operatoren: KENNUNG_OPERATOREN,
        auswahl: [
            { wert: "push", label: "Push" },
            { wert: "web", label: "Web" },
            { wert: "merge_request_event", label: "Merge Request" },
            { wert: "schedule", label: "Zeitplan" },
            { wert: "api", label: "API" },
            { wert: "trigger", label: "Trigger" },
            { wert: "pipeline", label: "Übergeordnete Pipeline" },
        ],
        freieAuswahl: true,
    },
    "pipeline.ausloeser_name": {
        label: "Pipeline-Auslösername",
        gruppe: "Pipeline",
        beschreibung: "Anzeigename der auslösenden GitLab-Person.",
        datentyp: "text",
        zielArten: ["pipeline"],
        operatoren: TEXT_OPERATOREN,
    },
    "pipeline.ausloeser_username": {
        label: "Pipeline-Auslöserkennung",
        gruppe: "Pipeline",
        beschreibung: "GitLab-Benutzerkennung der auslösenden Person.",
        datentyp: "kennung",
        zielArten: ["pipeline"],
        operatoren: KENNUNG_OPERATOREN,
    },
    "pipeline.dauer_sekunden": {
        label: "Pipeline-Dauer",
        gruppe: "Pipeline",
        beschreibung: "Gemeldete Gesamtdauer in Sekunden.",
        datentyp: "zahl",
        zielArten: ["pipeline"],
        operatoren: ZAHL_OPERATOREN,
    },
    "auslieferung.name": {
        label: "Auslieferungsname",
        gruppe: "Auslieferung",
        beschreibung: "Name der Auslieferung.",
        datentyp: "text",
        zielArten: ["auslieferung"],
        operatoren: TEXT_OPERATOREN,
    },
    "auslieferung.version": {
        label: "Auslieferungsversion",
        gruppe: "Auslieferung",
        beschreibung: "Version oder Tag der Auslieferung.",
        datentyp: "kennung",
        zielArten: ["auslieferung"],
        operatoren: KENNUNG_OPERATOREN,
    },
};
export const QA_BEDINGUNGS_OPERATOR_LABEL = {
    ist: "ist",
    ist_nicht: "ist nicht",
    enthaelt: "enthält",
    enthaelt_nicht: "enthält nicht",
    beginnt_mit: "beginnt mit",
    endet_mit: "endet mit",
    ist_groesser_als: "ist größer als",
    ist_kleiner_als: "ist kleiner als",
    mindestens: "ist mindestens",
    hoechstens: "ist höchstens",
    ist_wahr: "ist wahr",
    ist_falsch: "ist falsch",
    ist_leer: "ist leer",
    ist_nicht_leer: "ist nicht leer",
};
export const QA_BEDINGUNGS_VERKNUEPFUNG_LABEL = {
    alle: "Alle",
    mindestens_eine: "Mindestens eine",
    keine: "Keine",
};
const knotenIdSchema = z.string().trim().min(1).max(100);
const bedingungswertSchema = z.union([
    z.string().max(500),
    z.number().finite(),
    z.boolean(),
    z.null(),
]);
export const qaBedingungsvergleichSchema = z
    .object({
    id: knotenIdSchema,
    art: z.literal("vergleich"),
    feld: z.enum(Object.keys(QA_BEDINGUNGS_FELDER)),
    operator: z.enum(QA_BEDINGUNGS_OPERATOREN),
    wert: bedingungswertSchema.optional(),
})
    .strict();
export const qaBedingungsknotenSchema = z.lazy(() => z.union([qaBedingungsvergleichSchema, qaBedingungsgruppeSchema]));
export const qaBedingungsgruppeSchema = z
    .object({
    id: knotenIdSchema,
    art: z.literal("gruppe"),
    verknuepfung: z.enum(QA_BEDINGUNGS_VERKNUEPFUNGEN),
    /** Auch breite Ausdrücke bleiben in Editor und Motor begrenzt. */
    kinder: z.array(qaBedingungsknotenSchema).max(30),
})
    .strict();
export const QA_BEDINGUNGEN_HOECHSTENS = 30;
export const QA_BEDINGUNGSGRUPPEN_HOECHSTENS = 10;
export const QA_BEDINGUNGSEBENEN_HOECHSTENS = 3;
export function leereQaBedingungsgruppe() {
    return {
        id: "wurzel",
        art: "gruppe",
        verknuepfung: "alle",
        kinder: [],
    };
}
export const LEERE_QA_BEDINGUNGSGRUPPE = Object.freeze(leereQaBedingungsgruppe());
/**
 * Die einzige bedeutungslose Mehrdeutigkeit des Modells entfernen: Eine leere
 * Wurzel bedeutet immer „jeder passende Anlass" und wird deshalb als `alle`
 * gespeichert. Untergruppen werden hier nicht umgeschrieben; leer sind sie ein
 * fachlicher Fehler.
 */
export function kanonisiereQaBedingungen(gruppe) {
    return gruppe.kinder.length === 0 && gruppe.verknuepfung !== "alle"
        ? { ...gruppe, verknuepfung: "alle" }
        : gruppe;
}
export function istQaBedingungsfeld(wert) {
    return Object.hasOwn(QA_BEDINGUNGS_FELDER, wert);
}
export function operatorBrauchtWert(operator) {
    return ![
        "ist_wahr",
        "ist_falsch",
        "ist_leer",
        "ist_nicht_leer",
    ].includes(operator);
}
export function felderFuerQaZielarten(zielArten) {
    return Object.keys(QA_BEDINGUNGS_FELDER).filter((feld) => QA_BEDINGUNGS_FELDER[feld].zielArten.some((art) => zielArten.includes(art)));
}
function wertIstGueltig(wert, definition) {
    switch (definition.datentyp) {
        case "zahl":
            return typeof wert === "number" && Number.isFinite(wert);
        case "wahrheitswert":
            return typeof wert === "boolean";
        case "text":
        case "kennung":
        case "auswahl":
        case "textliste":
            return typeof wert === "string" && wert.trim().length > 0;
    }
}
/**
 * Prüft Form, Grenzen, Feldverfügbarkeit und die Operator-/Wertmatrix.
 *
 * `zielArten` sind die Zielarten, die der gewählte Anlass grundsätzlich tragen
 * kann. Bei allgemeinen Cubic-Vorgängen sind das alle drei.
 */
export function pruefeQaBedingungen(roh, zielArten = QA_BEDINGUNGS_ZIELARTEN) {
    const form = qaBedingungsgruppeSchema.safeParse(roh);
    if (!form.success) {
        return form.error.issues.map((problem) => ({
            knotenId: "wurzel",
            code: "ungueltige_struktur",
            text: `Ungültiger Bedingungsausdruck: ${problem.message}`,
        }));
    }
    const maengel = [];
    const ids = new Set();
    let gruppen = 0;
    let vergleiche = 0;
    function besuche(knoten, ebene) {
        if (ids.has(knoten.id)) {
            maengel.push({
                knotenId: knoten.id,
                code: "doppelte_kennung",
                text: "Jede Bedingung und Gruppe braucht eine eindeutige Kennung.",
            });
        }
        ids.add(knoten.id);
        if (knoten.art === "gruppe" &&
            ebene > QA_BEDINGUNGSEBENEN_HOECHSTENS) {
            maengel.push({
                knotenId: knoten.id,
                code: "zu_tief",
                text: `Bedingungen dürfen höchstens ${QA_BEDINGUNGSEBENEN_HOECHSTENS} Gruppenebenen tief sein.`,
            });
        }
        if (knoten.art === "gruppe") {
            gruppen += 1;
            if (ebene > 1 && knoten.kinder.length === 0) {
                maengel.push({
                    knotenId: knoten.id,
                    code: "leere_untergruppe",
                    text: "Eine Untergruppe braucht mindestens eine Bedingung.",
                });
            }
            for (const kind of knoten.kinder)
                besuche(kind, ebene + 1);
            return;
        }
        vergleiche += 1;
        const definition = QA_BEDINGUNGS_FELDER[knoten.feld];
        if (!definition.zielArten.some((art) => zielArten.includes(art))) {
            maengel.push({
                knotenId: knoten.id,
                code: "feld_nicht_verfuegbar",
                text: `${definition.label} steht für diesen Anlass nicht zur Verfügung.`,
            });
        }
        if (!definition.operatoren.includes(knoten.operator)) {
            maengel.push({
                knotenId: knoten.id,
                code: "operator_passt_nicht",
                text: `„${QA_BEDINGUNGS_OPERATOR_LABEL[knoten.operator]}“ passt nicht zu ${definition.label}.`,
            });
            return;
        }
        if (!operatorBrauchtWert(knoten.operator)) {
            if ("wert" in knoten) {
                maengel.push({
                    knotenId: knoten.id,
                    code: "wert_passt_nicht",
                    text: `„${QA_BEDINGUNGS_OPERATOR_LABEL[knoten.operator]}“ verwendet keinen Vergleichswert.`,
                });
            }
            return;
        }
        if (!("wert" in knoten)) {
            maengel.push({
                knotenId: knoten.id,
                code: "wert_fehlt",
                text: `${definition.label} braucht einen Vergleichswert.`,
            });
            return;
        }
        if (!wertIstGueltig(knoten.wert, definition)) {
            maengel.push({
                knotenId: knoten.id,
                code: "wert_passt_nicht",
                text: `Der Vergleichswert passt nicht zu ${definition.label}.`,
            });
            return;
        }
        if (definition.datentyp === "auswahl" &&
            definition.auswahl &&
            definition.freieAuswahl === false &&
            !definition.auswahl.some((option) => option.wert === knoten.wert)) {
            maengel.push({
                knotenId: knoten.id,
                code: "wert_passt_nicht",
                text: `Der Wert gehört nicht zur Auswahl für ${definition.label}.`,
            });
        }
    }
    besuche(form.data, 1);
    if (gruppen > QA_BEDINGUNGSGRUPPEN_HOECHSTENS) {
        maengel.push({
            knotenId: form.data.id,
            code: "zu_viele_gruppen",
            text: `Ein Ausdruck darf höchstens ${QA_BEDINGUNGSGRUPPEN_HOECHSTENS} Gruppen enthalten.`,
        });
    }
    if (vergleiche > QA_BEDINGUNGEN_HOECHSTENS) {
        maengel.push({
            knotenId: form.data.id,
            code: "zu_viele_bedingungen",
            text: `Ein Ausdruck darf höchstens ${QA_BEDINGUNGEN_HOECHSTENS} Vergleiche enthalten.`,
        });
    }
    return maengel;
}
function istLeer(wert) {
    return (wert === undefined ||
        wert === null ||
        (typeof wert === "string" && wert.length === 0) ||
        (Array.isArray(wert) && wert.length === 0));
}
function klein(wert) {
    return wert.toLocaleLowerCase("de-DE");
}
function werteVergleichAus(vergleich, aktuell) {
    if (vergleich.operator === "ist_leer")
        return istLeer(aktuell);
    if (istLeer(aktuell))
        return false;
    if (vergleich.operator === "ist_nicht_leer")
        return true;
    const definition = QA_BEDINGUNGS_FELDER[vergleich.feld];
    if (definition.datentyp === "wahrheitswert") {
        return vergleich.operator === "ist_wahr" ? aktuell === true : aktuell === false;
    }
    if (definition.datentyp === "zahl") {
        if (typeof aktuell !== "number" || typeof vergleich.wert !== "number") {
            return false;
        }
        switch (vergleich.operator) {
            case "ist":
                return aktuell === vergleich.wert;
            case "ist_nicht":
                return aktuell !== vergleich.wert;
            case "ist_groesser_als":
                return aktuell > vergleich.wert;
            case "ist_kleiner_als":
                return aktuell < vergleich.wert;
            case "mindestens":
                return aktuell >= vergleich.wert;
            case "hoechstens":
                return aktuell <= vergleich.wert;
            default:
                return false;
        }
    }
    const erwartet = typeof vergleich.wert === "string" ? vergleich.wert : "";
    if (definition.datentyp === "textliste") {
        if (!Array.isArray(aktuell))
            return false;
        const enthalten = aktuell.some((eintrag) => klein(eintrag) === klein(erwartet));
        return vergleich.operator === "enthaelt_nicht" ? !enthalten : enthalten;
    }
    if (typeof aktuell !== "string")
        return false;
    const textVergleich = definition.datentyp === "text";
    const ist = textVergleich ? klein(aktuell) : aktuell;
    const soll = textVergleich ? klein(erwartet) : erwartet;
    switch (vergleich.operator) {
        case "ist":
            return ist === soll;
        case "ist_nicht":
            return ist !== soll;
        case "enthaelt":
            return ist.includes(soll);
        case "enthaelt_nicht":
            return !ist.includes(soll);
        case "beginnt_mit":
            return ist.startsWith(soll);
        case "endet_mit":
            return ist.endsWith(soll);
        default:
            return false;
    }
}
function wertText(wert) {
    if (typeof wert === "boolean")
        return wert ? "wahr" : "falsch";
    if (typeof wert === "number")
        return new Intl.NumberFormat("de-DE").format(wert);
    if (Array.isArray(wert))
        return wert.length > 0 ? wert.join(", ") : "leer";
    if (wert === null || wert === undefined || wert === "")
        return "‹Wert fehlt›";
    return `„${String(wert)}“`;
}
export function beschreibeQaVergleich(vergleich) {
    const definition = QA_BEDINGUNGS_FELDER[vergleich.feld];
    const operator = QA_BEDINGUNGS_OPERATOR_LABEL[vergleich.operator];
    const wert = operatorBrauchtWert(vergleich.operator)
        ? ` ${wertText(vergleich.wert)}`
        : "";
    return `${definition.label} ${operator}${wert}`;
}
function beschreibeGruppe(gruppe, istWurzel) {
    if (gruppe.kinder.length === 0)
        return "ohne zusätzliche Bedingung";
    const kinder = gruppe.kinder.map((kind) => kind.art === "gruppe"
        ? `(${beschreibeGruppe(kind, false)})`
        : beschreibeQaVergleich(kind));
    if (gruppe.verknuepfung === "keine") {
        return `keine davon gilt: ${kinder.join("; ")}`;
    }
    const verbindung = gruppe.verknuepfung === "alle" ? " und " : " oder ";
    const text = kinder.join(verbindung);
    if (!istWurzel)
        return text;
    return gruppe.verknuepfung === "alle"
        ? text
        : `mindestens eine gilt: ${text}`;
}
export function beschreibeQaBedingungen(gruppe) {
    return beschreibeGruppe(gruppe, true);
}
/** Wertet einen zuvor geprüften Ausdruck aus und liefert den vollständigen Baum. */
export function werteQaBedingungenAus(gruppe, kontext) {
    function werteKnotenAus(knoten) {
        if (knoten.art === "vergleich") {
            const aktuellerWert = kontext.werte[knoten.feld];
            const auswertung = {
                id: knoten.id,
                art: "vergleich",
                erfuellt: werteVergleichAus(knoten, aktuellerWert),
                text: beschreibeQaVergleich(knoten),
                feld: knoten.feld,
                operator: knoten.operator,
                aktuellerWert,
            };
            if ("wert" in knoten)
                auswertung.erwarteterWert = knoten.wert;
            return auswertung;
        }
        const kinder = knoten.kinder.map(werteKnotenAus);
        const erfuellt = kinder.length === 0
            ? true
            : knoten.verknuepfung === "alle"
                ? kinder.every((kind) => kind.erfuellt)
                : knoten.verknuepfung === "mindestens_eine"
                    ? kinder.some((kind) => kind.erfuellt)
                    : !kinder.some((kind) => kind.erfuellt);
        return {
            id: knoten.id,
            art: "gruppe",
            erfuellt,
            text: beschreibeGruppe(knoten, knoten.id === gruppe.id),
            verknuepfung: knoten.verknuepfung,
            kinder,
        };
    }
    return werteKnotenAus(gruppe);
}
export function zaehleQaBedingungsknoten(gruppe) {
    let gruppen = 0;
    let vergleiche = 0;
    function besuche(knoten) {
        if (knoten.art === "vergleich") {
            vergleiche += 1;
            return;
        }
        gruppen += 1;
        knoten.kinder.forEach(besuche);
    }
    besuche(gruppe);
    return { gruppen, vergleiche };
}
export function findeQaBedingungsknoten(gruppe, id) {
    if (gruppe.id === id)
        return gruppe;
    for (const kind of gruppe.kinder) {
        if (kind.id === id)
            return kind;
        if (kind.art === "gruppe") {
            const treffer = findeQaBedingungsknoten(kind, id);
            if (treffer)
                return treffer;
        }
    }
    return null;
}
export function ersetzeQaBedingungsknoten(gruppe, id, ersatz) {
    if (gruppe.id === id) {
        return ersatz.art === "gruppe" ? ersatz : gruppe;
    }
    return {
        ...gruppe,
        kinder: gruppe.kinder.map((kind) => {
            if (kind.id === id)
                return ersatz;
            return kind.art === "gruppe"
                ? ersetzeQaBedingungsknoten(kind, id, ersatz)
                : kind;
        }),
    };
}
export function fuegeQaBedingungsknotenHinzu(gruppe, gruppenId, knoten) {
    if (gruppe.id === gruppenId) {
        return { ...gruppe, kinder: [...gruppe.kinder, knoten] };
    }
    return {
        ...gruppe,
        kinder: gruppe.kinder.map((kind) => kind.art === "gruppe"
            ? fuegeQaBedingungsknotenHinzu(kind, gruppenId, knoten)
            : kind),
    };
}
export function entferneQaBedingungsknoten(gruppe, id) {
    if (gruppe.id === id)
        return gruppe;
    return {
        ...gruppe,
        kinder: gruppe.kinder
            .filter((kind) => kind.id !== id)
            .map((kind) => kind.art === "gruppe" ? entferneQaBedingungsknoten(kind, id) : kind),
    };
}
export function verschiebeQaBedingungsknoten(gruppe, id, richtung) {
    const index = gruppe.kinder.findIndex((kind) => kind.id === id);
    if (index >= 0) {
        const ziel = index + richtung;
        if (ziel < 0 || ziel >= gruppe.kinder.length)
            return gruppe;
        const kinder = [...gruppe.kinder];
        [kinder[index], kinder[ziel]] = [kinder[ziel], kinder[index]];
        return { ...gruppe, kinder };
    }
    return {
        ...gruppe,
        kinder: gruppe.kinder.map((kind) => kind.art === "gruppe"
            ? verschiebeQaBedingungsknoten(kind, id, richtung)
            : kind),
    };
}
export function dupliziereQaBedingungsknoten(knoten, neueId) {
    if (knoten.art === "vergleich")
        return { ...knoten, id: neueId() };
    return {
        ...knoten,
        id: neueId(),
        kinder: knoten.kinder.map((kind) => dupliziereQaBedingungsknoten(kind, neueId)),
    };
}
export function sichereVorgabeFuerQaFeld(feld, id) {
    const definition = QA_BEDINGUNGS_FELDER[feld];
    const operator = definition.operatoren[0] ?? "ist";
    const basis = { id, art: "vergleich", feld, operator };
    if (!operatorBrauchtWert(operator))
        return basis;
    if (definition.datentyp === "zahl")
        return { ...basis, wert: 0 };
    if (definition.datentyp === "wahrheitswert")
        return basis;
    return {
        ...basis,
        wert: definition.auswahl?.[0]?.wert ?? "",
    };
}
//# sourceMappingURL=auto-qa-bedingungen.js.map